import { getFirestore, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { app } from '../firebase';

const db = getFirestore(app);

export const adminOverviewService = {
  /**
   * Get overview statistics
   * @returns {Promise<Object>} Overview statistics
   */
  async getOverviewStats() {
    try {
      const stats = {
        occupancy: { current: 0, total: 18 },
        activeDeliveryOrders: 0,
        inHousePreOrders: 0,
        totalUsers: 0,
        totalRevenue: 0,
        todayRevenue: 0,
        weeklyRevenue: 0,
        monthlyRevenue: 0
      };

      // Get current date for filtering
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());

      // Fetch delivery orders (active orders)
      const deliveryOrdersQuery = query(
        collection(db, 'deliveryOrders'),
        orderBy('createdAt', 'desc'),
        limit(1000)
      );
      const deliverySnapshot = await getDocs(deliveryOrdersQuery);
      
      deliverySnapshot.forEach(doc => {
        const orderData = doc.data();
        const orderDate = orderData.createdAt?.toDate?.() || orderData.createdAt;
        
        // Count active orders (not completed or cancelled)
        if (orderData.status && !['completed', 'cancelled', 'delivered'].includes(orderData.status)) {
          stats.activeDeliveryOrders++;
        }
        
        // Calculate revenue - handle both 'total' and 'totalAmount' fields
        const orderTotal = orderData.total || orderData.totalAmount || 0;
        if (orderTotal > 0) {
          stats.totalRevenue += orderTotal;
          
          if (orderDate && new Date(orderDate) >= today) {
            stats.todayRevenue += orderTotal;
          }
          if (orderDate && new Date(orderDate) >= weekAgo) {
            stats.weeklyRevenue += orderTotal;
          }
          if (orderDate && new Date(orderDate) >= monthAgo) {
            stats.monthlyRevenue += orderTotal;
          }
        }
      });

      // Fetch reservations to calculate occupancy
      const reservationsQuery = query(
        collection(db, 'reservations'),
        orderBy('createdAt', 'desc'),
        limit(1000)
      );
      const reservationsSnapshot = await getDocs(reservationsQuery);
      
      reservationsSnapshot.forEach(doc => {
        const reservationData = doc.data();
        const reservationDate = reservationData.date?.toDate?.() || reservationData.date;
        
        // Check if reservation is for today and active
        if (reservationDate && 
            new Date(reservationDate).toDateString() === today.toDateString() && 
            reservationData.status === 'confirmed') {
          stats.occupancy.current++;
        }
      });

      // Fetch pre-orders (in-house orders)
      const preOrdersQuery = query(
        collection(db, 'preOrders'),
        orderBy('createdAt', 'desc'),
        limit(1000)
      );
      const preOrdersSnapshot = await getDocs(preOrdersQuery);
      
      preOrdersSnapshot.forEach(doc => {
        const orderData = doc.data();
        const orderDate = orderData.createdAt?.toDate?.() || orderData.createdAt;
        
        // Count active in-house orders
        if (orderData.status && !['completed', 'cancelled'].includes(orderData.status)) {
          stats.inHousePreOrders++;
        }
        
        // Calculate revenue from pre-orders - handle both 'total' and 'totalAmount' fields
        const orderTotal = orderData.total || orderData.totalAmount || 0;
        if (orderTotal > 0) {
          stats.totalRevenue += orderTotal;
          
          if (orderDate && new Date(orderDate) >= today) {
            stats.todayRevenue += orderTotal;
          }
          if (orderDate && new Date(orderDate) >= weekAgo) {
            stats.weeklyRevenue += orderTotal;
          }
          if (orderDate && new Date(orderDate) >= monthAgo) {
            stats.monthlyRevenue += orderTotal;
          }
        }
      });

      // Fetch users count
      const usersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(1000)
      );
      const usersSnapshot = await getDocs(usersQuery);
      console.log(usersSnapshot);
      
      // Count only valid/active users (not deleted or disabled)
      let validUserCount = 0;
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        // Count users that are not deleted and have valid email
        if (userData && !userData.deleted && userData.email) {
          validUserCount++;
        }
      });
      stats.totalUsers = validUserCount;

      return stats;
      
    } catch (error) {
      console.error('Error fetching overview stats:', error);
      throw new Error(error.message || 'Failed to fetch overview statistics');
    }
  },

  /**
   * Get in-house orders for the dashboard
   * @param {number} limit - Number of orders to return
   * @returns {Promise<Array>} In-house orders
   */
  async getInHouseOrders(limitCount = 10) {
    try {
      const preOrdersQuery = query(
        collection(db, 'preOrders'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(preOrdersQuery);
      const orders = [];
      
      snapshot.forEach(doc => {
        const orderData = doc.data();
        orders.push({
          id: doc.id,
          customerName: orderData.customerName || orderData.userEmail || 'N/A',
          amount: orderData.total || orderData.totalAmount || 0,
          status: orderData.status || 'pending',
          createdAt: orderData.createdAt?.toDate?.() || orderData.createdAt,
          orderType: orderData.orderType || 'dine_in'
        });
      });

      return orders;
    } catch (error) {
      console.error('Error fetching in-house orders:', error);
      throw new Error(error.message || 'Failed to fetch in-house orders');
    }
  },

  /**
   * Get sales performance data
   * @param {string} period - 'daily', 'weekly', 'monthly'
   * @returns {Promise<Object>} Sales performance data
   */
  async getSalesPerformance(period = 'daily') {
    try {
      const now = new Date();
      let startDate;
      
      switch (period) {
        case 'daily':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
          break;
        case 'weekly': {
          // Get the start of the current week (Sunday) and go back 3 weeks
          const currentWeekStart = new Date(now);
          currentWeekStart.setDate(now.getDate() - now.getDay());
          currentWeekStart.setHours(0, 0, 0, 0);
          startDate = new Date(currentWeekStart);
          startDate.setDate(currentWeekStart.getDate() - 21); 
          break;
        }
        case 'monthly':
          // Get the start of the current month and go back 5 months
          startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6); 
      }



      // Fetch all orders from both collections
      const deliveryOrdersQuery = query(
        collection(db, 'deliveryOrders'),
        orderBy('createdAt', 'desc'),
        limit(1000)
      );
      const preOrdersQuery = query(
        collection(db, 'preOrders'),
        orderBy('createdAt', 'desc'),
        limit(1000)
      );
      
      const [deliverySnapshot, preOrdersSnapshot] = await Promise.all([
        getDocs(deliveryOrdersQuery),
        getDocs(preOrdersQuery)
      ]);



      // Initialize sales data
      const salesData = {};
      const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
      
      // Initialize sales data based on period
      if (period === 'daily') {
        // For daily: show last 7 days
        for (let i = 0; i < 7; i++) {
          const date = new Date(startDate);
          date.setDate(date.getDate() + i);
          const dayKey = dayNames[date.getDay()];
          salesData[dayKey] = 0;
        }
      } else if (period === 'weekly') {
        // For weekly: show last 4 weeks
        for (let i = 0; i < 4; i++) {
          const weekStart = new Date(startDate);
          weekStart.setDate(startDate.getDate() + (i * 7));
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          const weekKey = `Week ${i + 1}`;
          salesData[weekKey] = 0;
        }
      } else if (period === 'monthly') {
        // For monthly: show last 6 months
        for (let i = 0; i < 6; i++) {
          const monthDate = new Date(startDate);
          monthDate.setMonth(startDate.getMonth() + i);
          const monthKey = monthDate.toLocaleDateString('en-US', { month: 'short' });
          salesData[monthKey] = 0;
        }
      }



      // Process delivery orders
      deliverySnapshot.forEach(doc => {
        const orderData = doc.data();
        const orderDate = orderData.createdAt?.toDate?.() || orderData.createdAt;
        
        // Handle both 'total' and 'totalAmount' fields
        const orderTotal = orderData.total || orderData.totalAmount || 0;
        
        if (orderDate && new Date(orderDate) >= startDate && orderTotal > 0) {
          const orderDateObj = new Date(orderDate);
          let dayKey;
          
          if (period === 'daily') {
            dayKey = dayNames[orderDateObj.getDay()];
          } else if (period === 'weekly') {
            // Calculate which week this order belongs to
            const weekDiff = Math.floor((orderDateObj - startDate) / (1000 * 60 * 60 * 24 * 7));
            if (weekDiff >= 0 && weekDiff < 4) {
              dayKey = `Week ${weekDiff + 1}`;
            }
          } else if (period === 'monthly') {
            // Calculate which month this order belongs to
            const monthDiff = (orderDateObj.getFullYear() - startDate.getFullYear()) * 12 + 
                             (orderDateObj.getMonth() - startDate.getMonth());
            if (monthDiff >= 0 && monthDiff < 6) {
              dayKey = orderDateObj.toLocaleDateString('en-US', { month: 'short' });
            }
          }
          
          if (dayKey && Object.prototype.hasOwnProperty.call(salesData, dayKey)) {
            salesData[dayKey] += orderTotal;
          }
        }
      });

      // Process pre-orders
      preOrdersSnapshot.forEach(doc => {
        const orderData = doc.data();
        const orderDate = orderData.createdAt?.toDate?.() || orderData.createdAt;
        
        // Handle both 'total' and 'totalAmount' fields
        const orderTotal = orderData.total || orderData.totalAmount || 0;
        
        if (orderDate && new Date(orderDate) >= startDate && orderTotal > 0) {
          const orderDateObj = new Date(orderDate);
          let dayKey;
          
          if (period === 'daily') {
            dayKey = dayNames[orderDateObj.getDay()];
          } else if (period === 'weekly') {
            // Calculate which week this order belongs to
            const weekDiff = Math.floor((orderDateObj - startDate) / (1000 * 60 * 60 * 24 * 7));
            if (weekDiff >= 0 && weekDiff < 4) {
              dayKey = `Week ${weekDiff + 1}`;
            }
          } else if (period === 'monthly') {
            // Calculate which month this order belongs to
            const monthDiff = (orderDateObj.getFullYear() - startDate.getFullYear()) * 12 + 
                             (orderDateObj.getMonth() - startDate.getMonth());
            if (monthDiff >= 0 && monthDiff < 6) {
              dayKey = orderDateObj.toLocaleDateString('en-US', { month: 'short' });
            }
          }
          
          if (dayKey && Object.prototype.hasOwnProperty.call(salesData, dayKey)) {
            salesData[dayKey] += orderTotal;
          }
        }
      });

      return salesData;
      
    } catch (error) {
      console.error('Error fetching sales performance:', error);
      throw new Error(error.message || 'Failed to fetch sales performance');
    }
  },

  /**
   * Get top selling items
   * @param {number} limit - Number of items to return
   * @returns {Promise<Array>} Top selling items
   */
  async getTopSellingItems(limitCount = 5) {
    try {
      // Fetch all orders from both collections
      const deliveryOrdersQuery = query(
        collection(db, 'deliveryOrders'),
        orderBy('createdAt', 'desc'),
        limit(1000)
      );
      const preOrdersQuery = query(
        collection(db, 'preOrders'),
        orderBy('createdAt', 'desc'),
        limit(1000)
      );
      
      const [deliverySnapshot, preOrdersSnapshot] = await Promise.all([
        getDocs(deliveryOrdersQuery),
        getDocs(preOrdersQuery)
      ]);

      const itemSales = {};

      // Process delivery orders
      deliverySnapshot.forEach(doc => {
        const orderData = doc.data();
        if (orderData.dishes && Array.isArray(orderData.dishes)) {
          orderData.dishes.forEach(dish => {
            const itemName = dish.name || dish.dishName || 'Unknown Item';
            if (!itemSales[itemName]) {
              itemSales[itemName] = {
                name: itemName,
                totalSales: 0,
                quantity: 0,
                price: dish.price || 0
              };
            }
            itemSales[itemName].totalSales += (dish.price || 0) * (dish.quantity || 1);
            itemSales[itemName].quantity += dish.quantity || 1;
          });
        }
      });

      // Process pre-orders
      preOrdersSnapshot.forEach(doc => {
        const orderData = doc.data();
        if (orderData.items && Array.isArray(orderData.items)) {
          orderData.items.forEach(item => {
            const itemName = item.name || item.dishName || 'Unknown Item';
            if (!itemSales[itemName]) {
              itemSales[itemName] = {
                name: itemName,
                totalSales: 0,
                quantity: 0,
                price: item.price || 0
              };
            }
            itemSales[itemName].totalSales += (item.price || 0) * (item.quantity || 1);
            itemSales[itemName].quantity += item.quantity || 1;
          });
        }
      });

      // Convert to array and sort by total sales
      const topItems = Object.values(itemSales)
        .sort((a, b) => b.totalSales - a.totalSales)
        .slice(0, limitCount)
        .map(item => ({
          ...item,
          trend: Math.random() > 0.5 ? 'up' : 'down', // Mock trend for now
          trendPercentage: Math.floor(Math.random() * 20) + 1
        }));

      return topItems;
      
    } catch (error) {
      console.error('Error fetching top selling items:', error);
      throw new Error(error.message || 'Failed to fetch top selling items');
    }
  },

  /**
   * Get recent activity
   * @param {number} limit - Number of activities to return
   * @returns {Promise<Array>} Recent activities
   */
  async getRecentActivity(limitCount = 10) {
    try {
      const activities = [];

      // Fetch recent delivery orders
      const deliveryOrdersQuery = query(
        collection(db, 'deliveryOrders'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const deliverySnapshot = await getDocs(deliveryOrdersQuery);
      
      deliverySnapshot.forEach(doc => {
        const orderData = doc.data();
        // Handle both 'total' and 'totalAmount' fields
        const orderTotal = orderData.total || orderData.totalAmount || 0;
        activities.push({
          id: doc.id,
          type: 'delivery_order',
          title: `Delivery Order #${orderData.orderNumber || doc.id}`,
          description: `${orderData.customerName || orderData.userEmail} - $${orderTotal}`,
          status: orderData.status || 'pending',
          timestamp: orderData.createdAt?.toDate?.() || orderData.createdAt,
          amount: orderTotal
        });
      });

      // Fetch recent pre-orders
      const preOrdersQuery = query(
        collection(db, 'preOrders'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const preOrdersSnapshot = await getDocs(preOrdersQuery);
      
      preOrdersSnapshot.forEach(doc => {
        const orderData = doc.data();
        // Handle both 'total' and 'totalAmount' fields
        const orderTotal = orderData.total || orderData.totalAmount || 0;
        activities.push({
          id: doc.id,
          type: 'pre_order',
          title: `In-House Order #${orderData.orderNumber || doc.id}`,
          description: `${orderData.customerName || orderData.userEmail} - Table ${orderData.tableNumber || 'N/A'} - $${orderTotal}`,
          status: orderData.status || 'pending',
          timestamp: orderData.createdAt?.toDate?.() || orderData.createdAt,
          amount: orderTotal
        });
      });

      // Fetch recent reservations
      const reservationsQuery = query(
        collection(db, 'reservations'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const reservationsSnapshot = await getDocs(reservationsQuery);
      
      reservationsSnapshot.forEach(doc => {
        const reservationData = doc.data();
        activities.push({
          id: doc.id,
          type: 'reservation',
          title: `Reservation - ${reservationData.customerName || reservationData.userEmail}`,
          description: `${reservationData.date?.toDate?.() || reservationData.date} - ${reservationData.time || 'N/A'} - ${reservationData.guests || 1} guests`,
          status: reservationData.status || 'pending',
          timestamp: reservationData.createdAt?.toDate?.() || reservationData.createdAt
        });
      });

      // Sort all activities by timestamp and return top results
      return activities
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, limitCount);
      
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      throw new Error(error.message || 'Failed to fetch recent activity');
    }
  },

  /**
   * Format currency
   * @param {number} amount - Amount to format
   * @returns {string} Formatted currency
   */
  formatCurrency(amount) {
    return `${(amount || 0).toFixed(2)}`;
  },

  /**
   * Format date
   * @param {Date} date - Date to format
   * @returns {string} Formatted date
   */
  formatDate(date) {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  },

  /**
   * Format time
   * @param {Date} date - Date to format
   * @returns {string} Formatted time
   */
  formatTime(date) {
    if (!date) return 'N/A';
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  /**
   * Get status badge configuration
   * @returns {Object} Status badge styles
   */
  getStatusBadgeConfig() {
    return {
      pending: {
        bgColor: 'bg-yellow-500',
        textColor: 'text-black',
        label: 'Pending'
      },
      confirmed: {
        bgColor: 'bg-blue-500',
        textColor: 'text-white',
        label: 'Confirmed'
      },
      in_progress: {
        bgColor: 'bg-orange-500',
        textColor: 'text-white',
        label: 'In Progress'
      },
      completed: {
        bgColor: 'bg-green-500',
        textColor: 'text-white',
        label: 'Completed'
      },
      cancelled: {
        bgColor: 'bg-red-500',
        textColor: 'text-white',
        label: 'Cancelled'
      },
      delivered: {
        bgColor: 'bg-green-500',
        textColor: 'text-white',
        label: 'Delivered'
      }
    };
  }
};

export default adminOverviewService; 